import { DataSecurityMonitorType } from './DataSecurityMonitorType';

import { ComplianceCategory, DataSensitivityLevelId } from '../salesforce';

export type RecursivePartial<T> = NonNullable<T> extends object ? {
	[P in keyof T]?: NonNullable<T[P]> extends (infer U)[] ?
		RecursivePartial<U>[] : NonNullable<T[P]> extends object ? RecursivePartial<T[P]> : T[P];
} : T;

export enum MonitoringWindowType {
	Hourly = 'Hourly',
	Daily = 'Daily',
	Weekly = 'Weekly',
	Monthly = 'Monthly'
}

export enum MonitoringSchedule {
	Immediately = 'Immediately'
}

export type DataSecurityMonitorNotificationSettings = {
	isMuted?: boolean;
	stakeholderIds?: number[];
	notificationSchedule?: MonitoringSchedule;
};

export type DataSecurityMonitorBase<T = Record<string, unknown>> = DataSecurityMonitorNotificationSettings & {
	monitorConfig: T & { customLogic?: string; monitorTypeId: DataSecurityMonitorType };
	name: string;
	id: number;
	applicationId?: number;
	applicationIds?: Array<number>;
};

export type SetupAuditTrailRuleTypes = 'and' | 'or' | 'custom';

export type SetupAuditTrailConditionalLogicRule = {
	condition: 'classification' | 'section' | 'action' | 'user';
	order: number;
	value: Array<string> | string;
};

export type NewObjectAndFieldMonitor = DataSecurityMonitorBase<{
	blockAppsWhenAccessChanges: boolean;
}>;

export type UptimeMonitor = DataSecurityMonitorBase<{
	enableOnWeekends: boolean;
	monitoringWindow: MonitoringWindowType;
}>;

export type SetupAuditTrailRules = {
	ruleType: SetupAuditTrailRuleTypes;
	rules: Array<SetupAuditTrailConditionalLogicRule>;
};

export type SetupAuditTrailMonitor = DataSecurityMonitorBase<SetupAuditTrailRules>;

export type ConfigurableFieldAccessMonitor = DataSecurityMonitorBase<{
	dataSensitivityLevels: Array<DataSensitivityLevelId>;
	complianceCategories: ComplianceCategory;
}>;

export type AccessAnomalyMonitor = DataSecurityMonitorBase<{
	detectCountry: boolean;
	detectIpRange: boolean;
	detectUserAgentClient: boolean;
	detectUserAgentClientMajor: boolean;
	blockApplication: boolean;
}>;

export type AccessVolumeAnomalyMonitor = DataSecurityMonitorBase<{
	excludeWeekendsFromBaseline: boolean;
}>;

export type DataSecurityMonitor = DataSecurityMonitorBase | ConfigurableFieldAccessMonitor |
	UptimeMonitor | SetupAuditTrailMonitor | NewObjectAndFieldMonitor | AccessAnomalyMonitor |
	AccessVolumeAnomalyMonitor;

export type MonitorFormTypes = RecursivePartial<DataSecurityMonitor> & {
	overrideCount?: number;
	templateId?: number;
};
